import { useState, useEffect, useCallback, useRef } from "react";
import { useApp } from "@/lib/context";
import { getToken } from "@/lib/token-storage";
import { useQueryClient } from "@tanstack/react-query";
import { showBackButton, hideBackButton } from '@/lib/telegram-service';
import { ProfileType } from "@shared/schema";
import { HugeiconsIcon } from '@hugeicons/react';
import { Chat01Icon, FilterVerticalIcon } from '@hugeicons/core-free-icons';

// Profile Type configuration
const PROFILE_TYPES = [
  {
    id: ProfileType.COUPLE,
    name: "Couple",
    image: "/assets/images/gender/coppia.svg",
    iconColor: "text-purple-400",
    gradient: "radial-gradient(circle at bottom right, rgba(227, 5, 38, 0.8) 0%, rgba(227, 5, 38, 0.2) 30%, rgba(243, 244, 246, 0.05) 50%)"
  },
  {
    id: ProfileType.FEMALE,
    name: "Female",
    image: "/assets/images/gender/venus.svg",
    iconColor: "text-pink-400",
    gradient: "radial-gradient(circle at bottom right, rgba(236, 72, 153, 0.8) 0%, rgba(236, 72, 153, 0.2) 30%, rgba(243, 244, 246, 0.05) 50%)"
  },
  {
    id: ProfileType.MALE,
    name: "Male",
    image: "/assets/images/gender/mars.svg",
    iconColor: "text-blue-500",
    gradient: "radial-gradient(circle at bottom right, rgba(21, 101, 235, 0.8) 0%, rgba(21, 101, 235, 0.2) 30%, rgba(243, 244, 246, 0.05) 50%)"
  },
  {
    id: ProfileType.TRANS,
    name: "Trans",
    image: "/assets/images/gender/gender-transgender.svg",
    iconColor: "text-violet-400",
    gradient: "radial-gradient(circle at bottom right, rgba(168, 85, 247, 0.8) 0%, rgba(168, 85, 247, 0.2) 30%, rgba(243, 244, 246, 0.05) 50%)"
  },
  {
    id: ProfileType.TRAV,
    name: "Trav",
    image: "/assets/images/gender/gender-trasvesti.svg",
    iconColor: "text-indigo-400",
    gradient: "radial-gradient(circle at bottom right, rgba(192, 132, 252, 0.8) 0%, rgba(192, 132, 252, 0.2) 30%, rgba(243, 244, 246, 0.05) 50%)"
  }
];

// Get the color value for each category
const getIconColorValue = (iconColor: string): string => {
  switch (iconColor) {
    case 'text-purple-400': return '#a855f7';  // Purple
    case 'text-pink-400': return '#ec4899';    // Pink
    case 'text-blue-400': return '#60a5fa';    // Blue 400
    case 'text-blue-500': return '#3b82f6';    // Blue 500
    case 'text-indigo-400': return '#818cf8';  // Indigo
    case 'text-violet-400': return '#c084fc';  // Violet
    default: return '#ffffff';                  // White
  }
};

// Get CSS filter to colorize an icon to a specific color (from white/black)
const getColorFilter = (targetColor: string): string => {
  // No filters applied - icons use their original colors
  return 'none';
};

interface FilterSheetProps {
  isOpen: boolean;
  onClose: () => void;
  selectedFilters: string[];
  onFiltersChange: (filters: string[]) => void;
}

export function FilterSheet({ isOpen, onClose, selectedFilters, onFiltersChange }: FilterSheetProps) {
  const { user } = useApp();
  const queryClient = useQueryClient();
  const [animationPhase, setAnimationPhase] = useState<'initial' | 'expanding' | 'complete' | 'closing'>('initial');

  const getDescription = () => {
    switch (user?.language) {
      case 'it':
        return 'Seleziona i tipi di profilo che vuoi vedere. Solo gli utenti che hanno selezionato il tuo tipo saranno visibili (visibilità reciproca).';
      case 'es':
        return 'Selecciona los tipos de perfil que quieres ver. Solo los usuarios que también seleccionaron tu tipo serán visibles (visibilidad recíproca).';
      case 'de':
        return 'Wähle die Profiltypen aus, die du sehen möchtest. Nur Nutzer, die auch deinen Typ ausgewählt haben, werden sichtbar sein (gegenseitige Sichtbarkeit).';
      case 'pt':
        return 'Selecione os tipos de perfil que deseja ver. Apenas usuários que também selecionaram seu tipo serão visíveis (visibilidade recíproca).';
      case 'fr':
        return 'Sélectionnez les types de profil que vous souhaitez voir. Seuls les utilisateurs qui ont également sélectionné votre type seront visibles (visibilité réciproque).';
      default:
        return 'Select the profile types you want to see. Only users who also selected your type will be visible to you (reciprocal visibility).';
    }
  };

  const getSliderExplanation = () => {
    switch (user?.language) {
      case 'it':
        return 'Usa lo slider per impostare il numero minimo di validazioni richieste per iniziare una chat con te. Lascia a 0 per consentire a tutti di scriverti.';
      case 'es':
        return 'Usa el control deslizante para establecer el número mínimo de validaciones requeridas para iniciar un chat contigo. Déjalo en 0 para permitir que todos te escriban.';
      case 'de':
        return 'Verwende den Schieberegler, um die Mindestanzahl an Validierungen festzulegen, die erforderlich sind, um einen Chat mit dir zu starten. Lasse ihn auf 0, damit alle dir schreiben können.';
      case 'pt':
        return 'Use o controle deslizante para definir o número mínimo de validações necessárias para iniciar um chat com você. Deixe em 0 para permitir que todos escrevam para você.';
      case 'fr':
        return 'Utilisez le curseur pour définir le nombre minimum de validations requises pour démarrer un chat avec vous. Laissez à 0 pour permettre à tous de vous écrire.';
      default:
        return 'Use the slider to set the minimum number of validations required to start a chat with you. Leave at 0 to allow everyone to message you.';
    }
  };

  // Local state for filters (synced with props)
  const [localFilters, setLocalFilters] = useState<string[]>(selectedFilters);

  // Chat validation requirements state
  const [chatRequirements, setChatRequirements] = useState<Record<string, number | null>>({});

  // Track which filters are enabled
  const [enabledFilters, setEnabledFilters] = useState<Record<string, boolean>>({});

  // Initialize from user data
  useEffect(() => {
    if (user?.chatValidationRequirements) {
      setChatRequirements(user.chatValidationRequirements);
      // Initialize enabled filters based on whether value > 0
      const enabled: Record<string, boolean> = {};
      Object.entries(user.chatValidationRequirements).forEach(([key, val]) => {
        enabled[key] = (val as number) > 0;
      });
      setEnabledFilters(enabled);
    }
  }, [user?.chatValidationRequirements]);

  // Sync local filters with props
  useEffect(() => {
    setLocalFilters(selectedFilters);
  }, [selectedFilters]);

  // Handle close with animation
  const handleClose = useCallback(() => {
    setAnimationPhase('closing');
    setTimeout(() => {
      onClose();
      setAnimationPhase('initial');
    }, 300);
  }, [onClose]);

  // Ref to store back button ID
  const backButtonIdRef = useRef<string | null>(null);

  // Animation handling
  useEffect(() => {
    if (!isOpen) {
      setAnimationPhase('initial');
      // Hide back button with ID
      if (backButtonIdRef.current) {
        hideBackButton(backButtonIdRef.current);
        backButtonIdRef.current = null;
      }
    } else {
      setAnimationPhase('initial');
      requestAnimationFrame(() => {
        setAnimationPhase('expanding');
      });
      setTimeout(() => {
        setAnimationPhase('complete');
      }, 300);
      // Store the back button ID
      backButtonIdRef.current = showBackButton(handleClose);
    }

    // Cleanup on unmount
    return () => {
      if (backButtonIdRef.current) {
        hideBackButton(backButtonIdRef.current);
        backButtonIdRef.current = null;
      }
    };
  }, [isOpen, handleClose]);

  // Toggle profile type filter
  const toggleFilter = (profileTypeId: string) => {
    const newFilters = localFilters.includes(profileTypeId)
      ? localFilters.filter(f => f !== profileTypeId)
      : [...localFilters, profileTypeId];

    setLocalFilters(newFilters);
    onFiltersChange(newFilters);

    // Save to database
    saveVisibilityPreferences(newFilters);

    // Haptic feedback
    try {
      const tg = (window as any).Telegram?.WebApp;
      if (tg?.HapticFeedback) {
        tg.HapticFeedback.impactOccurred('light');
      }
    } catch (e) {
      // Ignore haptic errors
    }
  };

  // Save visibility preferences to database
  const saveVisibilityPreferences = async (filters: string[]) => {
    try {
      const token = getToken();
      if (!token) return;

      await fetch('/api/users/me/visibility-preferences', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ visibility_preferences: filters })
      });

      // Update React Query cache
      queryClient.setQueryData(['/api/auth/me'], (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          user: { ...oldData.user, visibility_preferences: filters }
        };
      });
    } catch (error) {
      console.error('[FilterSheet] Error saving visibility preferences:', error);
    }
  };

  // Toggle filter enabled/disabled
  const toggleFilterEnabled = async (profileType: string) => {
    const newEnabled = !enabledFilters[profileType];
    setEnabledFilters({ ...enabledFilters, [profileType]: newEnabled });

    // If disabling, set value to 0
    if (!newEnabled) {
      updateChatRequirement(profileType, 0);
    } else {
      // If enabling and value is 0, set to 1
      if ((chatRequirements[profileType] ?? 0) === 0) {
        updateChatRequirement(profileType, 1);
      }
    }
  };

  // Update chat requirement for a profile type
  const updateChatRequirement = async (profileType: string, value: number) => {
    const newRequirements = { ...chatRequirements, [profileType]: value };
    setChatRequirements(newRequirements);

    // Save to database
    try {
      const token = getToken();
      if (!token) return;

      await fetch('/api/users/me/chat-settings', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ chatValidationRequirements: newRequirements })
      });

      // Update React Query cache
      queryClient.setQueryData(['/api/auth/me'], (oldData: any) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          user: { ...oldData.user, chatValidationRequirements: newRequirements }
        };
      });
    } catch (error) {
      console.error('[FilterSheet] Error saving chat requirements:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 ${
          animationPhase === 'initial' || animationPhase === 'closing' ? 'opacity-0' : 'opacity-50'
        }`}
        style={{ zIndex: 70 }}
        onClick={handleClose}
      />

      {/* Sheet Container */}
      <div
        className={`fixed inset-x-0 bottom-0 tg-bg tg-text border-0 overflow-hidden transition-transform duration-300 ease-out ${
          animationPhase === 'initial' ? 'translate-y-full' :
          animationPhase === 'closing' ? 'translate-y-full' : 'translate-y-0'
        }`}
        style={{
          zIndex: 71,
          height: '100%',
          paddingTop: 'calc(90px + var(--safe-top))',
          borderTopLeftRadius: '24px',
          borderTopRightRadius: '24px'
        }}
      >
        {/* Content */}
        <div className="flex flex-col h-full">
          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6">

            {/* Section: Profile Types */}
            <div>
              {/* Titolo con icona filtro */}
              <div className="flex items-center gap-2 mb-3">
                <HugeiconsIcon
                  icon={FilterVerticalIcon}
                  className="w-5 h-5 text-white"
                  size={20}
                />
                <h2 className="text-lg font-semibold text-white">Match Filter</h2>
              </div>

              <p className="text-xs text-white mb-2 text-left">
                {getDescription()}
              </p>

              {/* Layout a 2 colonne */}
              <div className="grid grid-cols-2 gap-3 pt-6">
                {PROFILE_TYPES.map((type) => {
                  const isSelected = localFilters.includes(type.id);
                  const value = chatRequirements[type.id] ?? 0;

                  return (
                    <div key={type.id} className="flex flex-col overflow-hidden">
                      {/* Button con icona - layout orizzontale */}
                      <button
                        onClick={() => toggleFilter(type.id)}
                        className="rounded-xl transition-all duration-300 px-4 flex items-center gap-3 w-full"
                        style={{
                          background: isSelected ? type.gradient : '#252525',
                          height: '36px'
                        }}
                      >
                        {/* Icona - bianco se non selezionato, colore categoria se selezionato */}
                        {type.id === ProfileType.COUPLE ? (
                          // Coppia mantiene il suo colore originale
                          <img
                            src={type.image}
                            alt={type.name}
                            className="w-4 h-4 object-contain"
                          />
                        ) : (
                          // Altre icone usano mask per colorazione diretta
                          <div
                            className="w-4 h-4"
                            style={{
                              backgroundColor: isSelected ? getIconColorValue(type.iconColor) : '#ffffff',
                              maskImage: `url(${type.image})`,
                              maskSize: 'contain',
                              maskRepeat: 'no-repeat',
                              maskPosition: 'center',
                              WebkitMaskImage: `url(${type.image})`,
                              WebkitMaskSize: 'contain',
                              WebkitMaskRepeat: 'no-repeat',
                              WebkitMaskPosition: 'center'
                            }}
                          />
                        )}

                        {/* Label affiancata */}
                        <span className={`text-xs font-semibold transition-colors ${isSelected ? 'text-white' : 'text-white/60'}`}>
                          {type.name}
                        </span>
                      </button>

                      {/* Chat Requirements Slider - mostrato sotto */}
                      {isSelected && (
                        <div className="mt-2 px-4 flex items-center gap-2 w-full">
                          {/* Toggle Switch */}
                          <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                            <input
                              type="checkbox"
                              checked={enabledFilters[type.id] ?? false}
                              onChange={() => toggleFilterEnabled(type.id)}
                              className="sr-only peer"
                            />
                            <div className="w-7 h-4 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-[#1565eb]"></div>
                          </label>

                          {/* Icona Chat */}
                          <HugeiconsIcon icon={Chat01Icon} className="w-4 h-4 text-white/40 flex-shrink-0" size={16} />

                          {/* Slider - stessa larghezza del pulsante */}
                          <input
                            type="range"
                            min="0"
                            max="10"
                            value={value}
                            onChange={(e) => updateChatRequirement(type.id, parseInt(e.target.value))}
                            disabled={!enabledFilters[type.id]}
                            className="flex-grow-0 max-w-[calc(100%-84px)] h-1 bg-white/10 rounded-lg appearance-none cursor-pointer slider-thumb-small disabled:opacity-30 disabled:cursor-not-allowed"
                            style={{
                              background: `linear-gradient(to right, #1565eb 0%, #1565eb ${value * 10}%, rgba(255,255,255,0.1) ${value * 10}%, rgba(255,255,255,0.1) 100%)`
                            }}
                          />

                          {/* Counter */}
                          <span className={`text-xs font-bold text-white flex-shrink-0 w-4 text-right ${!enabledFilters[type.id] ? 'opacity-30' : ''}`}>
                            {value}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Spiegazione slider con icona */}
              <div className="mt-4 flex items-start gap-2">
                <HugeiconsIcon icon={Chat01Icon} className="w-4 h-4 text-white/40 flex-shrink-0 mt-0.5" size={16} />
                <p className="text-xs text-white/40 text-left">
                  {getSliderExplanation()}
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Custom slider thumb styles */}
      <style>{`
        .slider-thumb::-webkit-slider-thumb {
          appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          box-shadow: 0 1px 4px rgba(0,0,0,0.3);
        }
        .slider-thumb::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          border: none;
          box-shadow: 0 1px 4px rgba(0,0,0,0.3);
        }
        .slider-thumb-small::-webkit-slider-thumb {
          appearance: none;
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          box-shadow: 0 1px 3px rgba(0,0,0,0.3);
        }
        .slider-thumb-small::-moz-range-thumb {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          border: none;
          box-shadow: 0 1px 3px rgba(0,0,0,0.3);
        }
      `}</style>
    </>
  );
}

export default FilterSheet;
